/* Vivid Health theme — runtime behaviours.
   Cart: real Shopify cart.js API
   Sticky CTA: mirrors vivid/index.html .sticky-atc-- variants
   Reveal: IntersectionObserver-driven section fade-in */

const $  = (s, el = document) => el.querySelector(s);
const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));

let vividScrollY = 0;
let storefrontIsolation = [];
function storefrontOverlayOpen() {
  return Boolean(document.querySelector("#drawer.open, #mobnav.open, .v-shelf__qv.open"));
}
function lockStorefrontScroll() {
  if (document.body.classList.contains("vivid-scroll-locked")) return;
  vividScrollY = window.scrollY || window.pageYOffset || 0;
  document.body.style.top = `-${vividScrollY}px`;
  document.body.classList.add("vivid-scroll-locked");
}
function unlockStorefrontScroll() {
  if (storefrontOverlayOpen() || !document.body.classList.contains("vivid-scroll-locked")) return;
  document.body.classList.remove("vivid-scroll-locked");
  document.body.style.top = "";
  window.scrollTo(0, vividScrollY);
}
window.vividLockStorefrontScroll = lockStorefrontScroll;
window.vividUnlockStorefrontScroll = unlockStorefrontScroll;

function restoreStorefrontIsolation() {
  storefrontIsolation.forEach((state) => {
    state.node.inert = state.inert;
    if (state.ariaHidden === null) state.node.removeAttribute("aria-hidden");
    else state.node.setAttribute("aria-hidden", state.ariaHidden);
  });
  storefrontIsolation = [];
}
function isolateStorefrontOverlay(overlay, scrim) {
  restoreStorefrontIsolation();
  let keep = overlay;
  while (keep && keep !== document.body) {
    const parent = keep.parentElement;
    if (!parent) break;
    Array.from(parent.children)
      .filter((node) => node !== keep && node !== scrim && !["SCRIPT", "STYLE", "LINK"].includes(node.tagName))
      .forEach((node) => storefrontIsolation.push({ node, inert: node.inert, ariaHidden: node.getAttribute("aria-hidden") }));
    keep = parent;
  }
  storefrontIsolation.forEach((state) => {
    state.node.inert = true;
    state.node.setAttribute("aria-hidden", "true");
  });
}
window.vividIsolateStorefrontOverlay = isolateStorefrontOverlay;
window.vividRestoreStorefrontIsolation = restoreStorefrontIsolation;

/* ─── Shopify cart ─── */

async function cartFetch() {
  const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`Cart request failed (${r.status})`);
  return r.json();
}

async function cartAdd(id, qty = 1, sellingPlan = null) {
  const item = { id, quantity: qty };
  if (sellingPlan) item.selling_plan = sellingPlan;
  try {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [item] }),
    });
    const data = await r.json().catch(() => ({}));
    return { ...data, _ok: r.ok, _status: r.status };
  } catch (_error) {
    return { _ok: false, description: "Your connection dropped before the basket updated. Please try again." };
  }
}

/* Resolve the selected selling plan for a PDP add button (subscribe toggle). */
function selectedSellingPlan(btn) {
  if (!btn || !btn.closest || !btn.closest(".pdp-buybox")) return null;
  const toggle = document.querySelector("[data-sub-toggle]");
  if (!toggle) return null;
  const active = toggle.querySelector(".sub-opt.active");
  if (!active || active.dataset.purchase !== "subscribe") return null;
  const freq = document.querySelector("[data-sub-freq-select]");
  return (freq && freq.value) || active.dataset.sellingPlan || null;
}

window.vividCartAdd = async (id) => {
  const res = await cartAdd(id, 1);
  await renderCart();
  if (!res._ok) showCartNotice(res.description || res.message || "Couldn't add that to your basket — please check stock and try again.");
};

/* `id` is the cart line item's stable key (cart.items[].key), not a positional
   index — /cart/change.js also accepts an `id` (variant id or line item key)
   in place of the positional `line`, which avoids racing a queued click
   against a line shift caused by an earlier request that already resolved. */
async function cartChange(id, qty) {
  try {
    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id, quantity: qty }),
    });
    const data = await r.json().catch(() => ({}));
    return { ...data, _ok: r.ok, _status: r.status };
  } catch (_error) {
    return { _ok: false, description: "Your connection dropped before the basket updated. Please try again." };
  }
}

/* Show a non-blocking notice inside the open cart drawer (e.g. a failed
   add/change because of stock limits) — never fails silently. */
function showCartNotice(msg) {
  const body = $("#drawerBody");
  if (!body) { window.alert(msg); return; }
  const el = document.createElement("div");
  el.className = "cart-totals-note cart-totals-note--held";
  el.style.margin = "0 0 14px";
  el.textContent = msg;
  body.prepend(el);
  setTimeout(() => el.remove(), 6000);
}

const formatR = (cents) =>
  "R" + (cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2 }).replace(",", ".");

/* ─── One Life checkout handoff ───
   Nothing is sold on this store: the basket is native (this store's cart.js)
   but every order is taken by One Life Health — the fulfilment store with the
   branded checkout, Payflex instalments and the customer's account. Each
   basket line is translated to its One Life variant; a line that cannot carry
   (not yet listed there, awaiting stock, or a subscription set up over there)
   is held back and the customer is told — never silently dropped. */
const OL_BASE = "https://www.onelife.co.za";
const OL_MAP = {"vivid-allergy-stack-complete-allergy-support-bundle":{"v":53797391368502,"p":58900,"a":true},"vivid-bone-joint-pack-complete-skeletal-support-bundle":{"v":53797391401270,"p":83700,"a":true},"vivid-health-capsules-size-0-500-empty-bovine-capsules":{"v":44599597957430,"p":9706,"a":false},"vivid-health-capsules-size-00-500-empty-vegetable-capsules":{"v":44599597990198,"p":19073,"a":false},"vivid-health-gut-health-immune-wormwood-60-capsules":{"v":44599612309814,"p":13300,"a":true},"vivid-health-gut-health-immune-wormwood-60-capsules-1":{"v":53760861176118,"p":13300,"a":false},"vivid-health-gut-health-black-walnut-60-capsules":{"v":44599598940470,"p":14042,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-gut-health-black-walnut-60-capsules-1":{"v":53760863076662,"p":14042,"a":false},"vivid-health-gut-health-clove-60-capsules":{"v":44599600808246,"p":12328,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-gut-health-clove-60-capsules-1":{"v":53760862781750,"p":12328,"a":false},"vivid-health-gut-health-colon-flush-120-capsules":{"v":45593293193526,"p":19608,"a":true},"vivid-health-gut-health-colon-flush-120-capsules-1":{"v":53761030422838,"p":19608,"a":false},"6009689720135":{"v":53883071955254,"p":19608,"a":false},"vivid-health-gut-health-colon-flush-powder-135g":{"v":44599601496374,"p":14300,"a":true},"vivid-health-gut-health-colon-flush-powder-135g-1":{"v":53760862650678,"p":14300,"a":false},"vivid-health-immune-advanced-buffered-c-300-capsules":{"v":45763230990646,"p":55000,"a":true},"vivid-health-immune-advanced-buffered-c-300-capsules-1":{"v":53761015644470,"p":55000,"a":false},"6000000034818":{"v":53883058127158,"p":55000,"a":false},"vivid-health-immune-advanced-buffered-c-90-capsules":{"v":45763225059638,"p":20500,"a":false},"vivid-health-immune-advanced-buffered-c-90-capsules-1":{"v":53761015710006,"p":20500,"a":false},"vivid-health-immune-allergy-control-60-capsules":{"v":44599598022966,"p":19900,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-allergy-control-60-capsules-1":{"v":53760863273270,"p":19900,"a":false},"vivid-health-immune-astragalus-60-capsules":{"v":44599598285110,"p":16400,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-astragalus-60-capsules-1":{"v":53760863142198,"p":16400,"a":false},"vivid-health-immune-buffered-c-150g":{"v":44599599071542,"p":13283,"a":true},"vivid-health-immune-buffered-c-150g-1":{"v":53760862978358,"p":13283,"a":false},"vivid-health-immune-buffered-c-300-capsules":{"v":45593360433462,"p":48875,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-buffered-c-300-capsules-1":{"v":53761030390070,"p":48875,"a":false},"600000082009":{"v":53883071922486,"p":48875,"a":false},"vivid-health-immune-buffered-c-500g":{"v":44599599235382,"p":40112,"a":false},"vivid-health-immune-buffered-c-500g-1":{"v":53760862945590,"p":41000,"a":false},"vivid-health-immune-buffered-c-90-capsules":{"v":44599599431990,"p":17078,"a":true},"vivid-health-immune-buffered-c-90-capsules-1":{"v":53760862912822,"p":18976,"a":false},"vivid-health-immune-immune-plus-60-capsules":{"v":44599607230774,"p":12500,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-immune-plus-60-capsules-1":{"v":53760862028086,"p":12500,"a":false},"vivid-health-immune-l-lysine-60-capsules":{"v":44599607722294,"p":11800,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-l-lysine-60-capsules-1":{"v":53760861897014,"p":11800,"a":false},"vivid-health-immune-liver-kidney-formula-90-capsules":{"v":45070401536310,"p":24800,"a":false},"vivid-health-immune-liver-kidney-formula-90-capsules-1":{"v":53761045594422,"p":24800,"a":false},"6000000820374":{"v":53883084964150,"p":24800,"a":false},"vivid-health-immune-mullein-60-capsules":{"v":44599609819446,"p":13300,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-mullein-60-capsules-1":{"v":53760861602102,"p":13300,"a":false},"vivid-health-immune-quercetin-complex-60-capsules":{"v":44599610114358,"p":28520,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-immune-quercetin-complex-60-capsules-1":{"v":53760861503798,"p":28888,"a":false},"vivid-health-men-man-power-60-capsules":{"v":44599608344886,"p":15200,"a":false},"vivid-health-men-man-power-60-capsules-1":{"v":53760861831478,"p":15200,"a":false},"vivid-health-men-prosta-care-60-capsules":{"v":44599610016054,"p":26393,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-men-prosta-care-60-capsules-1":{"v":53760861536566,"p":26393,"a":false},"vivid-health-mens-health-ed-formula-510g":{"v":44599602676022,"p":38324,"a":false},"vivid-health-nutrient-health-turmeric-cayenne-60-capsules":{"v":46997792620854,"p":13248,"a":false},"vivid-health-nutrient-health-turmeric-cayenne-60-capsules-1":{"v":53760970948918,"p":13248,"a":false},"vivid-health-nutrient-health-turmeric-plus-60-capsules":{"v":46872291246390,"p":25760,"a":true},"vivid-health-nutrient-health-turmeric-plus-60-capsules-1":{"v":53760971079990,"p":26680,"a":false},"891368002313":{"v":53882978107702,"p":26680,"a":false},"vivid-health-nutritent-health-barley-grass-200g":{"v":44599598842166,"p":22400,"a":false},"vivid-health-nutritent-health-barley-grass-300-capsules":{"v":44599598874934,"p":28300,"a":false},"vivid-health-nutritent-health-cranberry-60-capsules":{"v":44599601725750,"p":10433,"a":false},"vivid-health-nutritent-health-xylitol-25kg":{"v":44599612408118,"p":47435,"a":false},"vivid-health-physical-health-l-glutamine-500g":{"v":46995137954102,"p":34500,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-physical-health-l-glutamine-500g-1":{"v":53760970981686,"p":34500,"a":false},"748927022803":{"v":53882978042166,"p":34500,"a":false},"vivid-health-physical-health-omega-oil-90-capsules":{"v":46994848350518,"p":23718,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-physical-health-omega-oil-90-capsules-1":{"v":53760971014454,"p":23718,"a":false},"6000000034597":{"v":53882978074934,"p":23718,"a":false},"vivid-health-stay-vivid-bacopa-60-capsules":{"v":44599598809398,"p":13300,"a":false},"vivid-health-stay-vivid-bacopa-60-capsules-1":{"v":53760863109430,"p":13300,"a":false},"vivid-health-stay-vivid-d-ribose-150g":{"v":44599602479414,"p":30740,"a":true},"vivid-health-stay-vivid-d-ribose-150g-1":{"v":53760862552374,"p":30740,"a":false},"vivid-health-stay-vivid-dmae-150g":{"v":44599602217270,"p":23569,"a":false},"vivid-health-stay-vivid-dmae-150g-1":{"v":53760862585142,"p":23569,"a":false},"vivid-health-stay-vivid-gaba-150g":{"v":44599604019510,"p":25800,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-stay-vivid-gaba-150g-1":{"v":53760862257462,"p":25800,"a":false},"vivid-health-stay-vivid-griffonia-5-htp-60-capsules":{"v":44599606575414,"p":27500,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-stay-vivid-griffonia-5-htp-60-capsules-1":{"v":53760862191926,"p":27500,"a":false},"vivid-health-stay-vivid-liquorice-root-60-capsules":{"v":44599607525686,"p":13299,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-stay-vivid-liquorice-root-60-capsules-1":{"v":53760861962550,"p":13299,"a":false},"vivid-health-stay-vivid-omega-oil-300-capsules":{"v":44599609884982,"p":68425,"a":false},"vivid-health-stay-vivid-omega-oil-300-capsules-1":{"v":53760861569334,"p":68425,"a":false},"vivid-health-stay-vivid-tranquil-60-capsules":{"v":44599611719990,"p":13000,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-stay-vivid-tranquil-60-capsules-1":{"v":53760861307190,"p":13000,"a":false},"vivid-health-vivid-body-bone-supreme-120-capsules":{"v":44599598973238,"p":43700,"a":true},"vivid-health-vivid-body-bone-supreme-120-capsules-1":{"v":53760863043894,"p":43700,"a":false},"vivid-health-vivid-body-bone-supreme-500-capsules":{"v":44599599006006,"p":125120,"a":false},"vivid-health-vivid-body-bone-supreme-500-capsules-1":{"v":53760863011126,"p":125120,"a":false},"vivid-health-vivid-body-coenzyme-q10-60-capsules":{"v":44599600939318,"p":34040,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-body-coenzyme-q10-60-capsules-1":{"v":53760862748982,"p":34960,"a":false},"vivid-health-vivid-body-colest-control-60-capsules":{"v":44599601103158,"p":23990,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-body-colest-control-60-capsules-1":{"v":53760862683446,"p":23990,"a":false},"vivid-health-vivid-body-flexijoint-300-capsules":{"v":44599603527990,"p":66300,"a":true},"vivid-health-vivid-body-flexijoint-300-capsules-1":{"v":53760862454070,"p":66300,"a":false},"vivid-health-vivid-body-flexijoint-advanced-120-capsules":{"v":44599603790134,"p":32200,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-body-flexijoint-advanced-120-capsules-1":{"v":53760862421302,"p":32200,"a":false},"vivid-health-vivid-body-garcinia-cambogia-60-capsules":{"v":44599606378806,"p":16606,"a":true},"vivid-health-vivid-body-garcinia-cambogia-60-capsules-1":{"v":53760862224694,"p":16606,"a":false},"vivid-health-vivid-body-joint-relief-60-capsules":{"v":44599607361846,"p":13800,"a":true},"vivid-health-vivid-body-joint-relief-60-capsules-1":{"v":53760861995318,"p":14352,"a":false},"vivid-health-vivid-body-msm-150g":{"v":44599609196854,"p":13685,"a":true},"vivid-health-vivid-body-msm-150g-1":{"v":53760861733174,"p":13685,"a":false},"vivid-health-vivid-body-msm-300-capsules":{"v":44599609360694,"p":39000,"a":true},"vivid-health-vivid-body-msm-300-capsules-1":{"v":53760861700406,"p":39000,"a":false},"vivid-health-vivid-body-msm-500g":{"v":44599609524534,"p":34960,"a":true},"vivid-health-vivid-body-msm-500g-1":{"v":53760861667638,"p":34960,"a":false},"vivid-health-vivid-body-msm-90-capsules":{"v":44599609688374,"p":17078,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-body-msm-90-capsules-1":{"v":53760861634870,"p":17078,"a":false},"vivid-health-vivid-nourishment-apple-cider-90-capsules":{"v":44599598154038,"p":19924,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-nourishment-apple-cider-90-capsules-1":{"v":53760863174966,"p":19924,"a":false},"vivid-health-vivid-nourishment-cayenne-250g":{"v":44599600382262,"p":13000,"a":true},"vivid-health-vivid-nourishment-cayenne-250g-1":{"v":53760862880054,"p":13000,"a":false},"vivid-health-vivid-nourishment-cayenne-300-capsules":{"v":44599600546102,"p":34200,"a":true},"vivid-health-vivid-nourishment-cayenne-300-capsules-1":{"v":53760862847286,"p":34200,"a":false},"vivid-health-vivid-nourishment-cayenne-90-capsules":{"v":44599600677174,"p":12880,"a":true},"vivid-health-vivid-nourishment-cayenne-90-capsules-1":{"v":53760862814518,"p":12880,"a":false},"vivid-health-vivid-nourishment-dextrose-1kg":{"v":44599602020662,"p":7604,"a":false},"vivid-health-vivid-nourishment-dextrose-1kg-1":{"v":53760862617910,"p":7604,"a":false},"vivid-health-vivid-nourishment-epsom-salt-1kg":{"v":44599602905398,"p":9099,"a":true},"vivid-health-vivid-nourishment-epsom-salt-1kg-1":{"v":53760862519606,"p":9099,"a":false},"vivid-health-vivid-nourishment-epsom-salt-25kg":{"v":44599603134774,"p":20700,"a":false},"vivid-health-vivid-nourishment-epsom-salt-25kg-1":{"v":53760862486838,"p":20700,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-course-500g":{"v":44599607099702,"p":3968,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-course-500g-1":{"v":53760862060854,"p":3968,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-course-1kg":{"v":44599606870326,"p":6900,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-course-1kg-1":{"v":53760862093622,"p":6900,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-fine-1kg":{"v":44599606640950,"p":6900,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-fine-1kg-1":{"v":53760862159158,"p":6900,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-fine-500g":{"v":44599606706486,"p":3835,"a":false},"vivid-health-vivid-nourishment-himalayan-salt-fine-500g-1":{"v":53760862126390,"p":3835,"a":false},"vivid-health-vivid-nourishment-moringa-powder-250g":{"v":44599609065782,"p":18400,"a":false},"vivid-health-vivid-nourishment-moringa-powder-250g-1":{"v":53760861765942,"p":18400,"a":false},"vivid-health-vivid-nourishment-moringa-powder-300-capsules":{"v":44599608901942,"p":29808,"a":true},"vivid-health-vivid-nourishment-moringa-powder-300-capsules-1":{"v":53760861798710,"p":29808,"a":false},"vivid-health-vivid-nourishment-sodium-bicarbonate-1kg":{"v":44599610704182,"p":8030,"a":false},"vivid-health-vivid-nourishment-sodium-bicarbonate-1kg-1":{"v":53760861438262,"p":8031,"a":false},"vivid-health-vivid-nourishment-spirulina-250g":{"v":44599611425078,"p":18584,"a":true},"vivid-health-vivid-nourishment-spirulina-250g-1":{"v":53760861405494,"p":18584,"a":false},"vivid-health-vivid-nourishment-spirulina-300-capsules":{"v":44599611588918,"p":25800,"a":false},"vivid-health-vivid-nourishment-spirulina-300-capsules-1":{"v":53760861372726,"p":25800,"a":false},"vivid-health-vivid-nourishment-turmeric-250g":{"v":44599611883830,"p":10351,"a":false},"vivid-health-vivid-nourishment-turmeric-250g-1":{"v":53760861274422,"p":10351,"a":false},"vivid-health-vivid-nourishment-turmeric-300-capsules":{"v":44599612047670,"p":30188,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-vivid-nourishment-turmeric-300-capsules-1":{"v":53760861241654,"p":30188,"a":false},"vivid-health-vivid-nourishment-turmeric-60-capsules":{"v":44599612178742,"p":12339,"a":false},"vivid-health-vivid-nourishment-turmeric-60-capsules-1":{"v":53760861208886,"p":12340,"a":false},"vivid-health-vivid-nourishment-xylitol-1kg":{"v":44599612342582,"p":22132,"a":false},"vivid-health-woman-angus-castus-60-capsules":{"v":44599598055734,"p":18000,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-woman-angus-castus-60-capsules-1":{"v":53760863207734,"p":18000,"a":false},"vivid-health-woman-maca-60-capsules":{"v":44599608181046,"p":17986,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-woman-maca-60-capsules-1":{"v":53760861864246,"p":17986,"a":false},"vivid-health-woman-sage-60-capsules":{"v":44599610573110,"p":12061,"a":true,"plans":{"30":8761180470,"60":8761213238,"90":8761246006}},"vivid-health-woman-sage-60-capsules-1":{"v":53760861471030,"p":12061,"a":false},"vivid-joint-family":{"v":54185914040630,"p":71500,"a":true},"vivid-rest-focus-stack-sleep-calm-and-mental-clarity-bundle":{"v":53797391434038,"p":59700,"a":true}};

let olMapFresh = false;
let resolveOlMapReady;
const olMapReady = new Promise((resolve) => { resolveOlMapReady = resolve; });

function olHandleOf(item) {
  if (item.handle) return item.handle;
  return ((item.url || "").split("/products/")[1] || "").split("?")[0].replace(/\/$/, "");
}

function olPlanFor(item, m) {
  const alloc = item.selling_plan_allocation;
  if (!alloc || !alloc.selling_plan) return { plan: null, ok: true };
  if (!m || !m.plans) return { plan: null, ok: false };
  const name = String(alloc.selling_plan.name || "");
  const days = /90/.test(name) ? "90" : /60/.test(name) ? "60" : "30";
  return { plan: m.plans[days] || null, ok: Boolean(m.plans[days]) };
}

function olHandoff(cart) {
  const carried = [];
  const held = [];
  if (!olMapFresh) {
    cart.items.forEach((item) => held.push({ item, why: "live One Life price and stock could not be confirmed" }));
    return { carried, held, total: cart.total_price, url: null };
  }
  cart.items.forEach((item) => {
    const m = OL_MAP[olHandleOf(item)];
    if (!m) { held.push({ item, why: "not yet on the One Life shelf" }); return; }
    if (!m.a) { held.push({ item, why: "out of stock at One Life" }); return; }
    const sub = olPlanFor(item, m);
    if (!sub.ok) { held.push({ item, why: "this subscription frequency isn't set up at One Life yet" }); return; }
    carried.push({ item, m, plan: sub.plan });
  });
  const qs = carried
    .map((c, i) =>
      "items[" + i + "][id]=" + c.m.v +
      "&items[" + i + "][quantity]=" + c.item.quantity +
      (c.plan ? "&items[" + i + "][selling_plan]=" + c.plan : ""))
    .join("&");
  const total = carried.reduce((sum, c) => sum + c.m.p * c.item.quantity, 0);
  return { carried, held, total, url: qs ? OL_BASE + "/cart/add?" + qs + "&return_to=/cart" : null };
}
window.olHandoff = olHandoff;

/* The embedded map is refreshed at deployment for fast rendering, but it is
   never sale-authoritative. Checkout waits for the live CDN asset generated
   by the One Life catalogue sync, preventing stale price/stock handoffs. */
(function refreshOlMap() {
  if (!window.OL_MAP_URL) { resolveOlMapReady(false); return; }
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  fetch(window.OL_MAP_URL, { cache: "no-cache", signal: controller.signal })
    .then((r) => (r.ok ? r.json() : null))
    .then((fresh) => {
      if (!fresh) return false;
      Object.keys(OL_MAP).forEach((k) => { if (!fresh[k]) delete OL_MAP[k]; });
      Object.assign(OL_MAP, fresh);
      olMapFresh = true;
      if (document.querySelector("#drawer.open")) renderCart();
      const bn = document.querySelector(".pdp-buy-now");
      if (bn) syncBuyNowToOL(bn);
      return true;
    })
    .catch(() => false)
    .finally(() => window.clearTimeout(timeout))
    .then(resolveOlMapReady);
})();

const OL_WHATSAPP = (items) =>
  "https://wa.me/27713744910?text=" +
  encodeURIComponent("Hi — I'd like to order: " + items.map((i) => i.product_title).join(", "));

function setPip(count) {
  const btn = $("#cartBtn");
  if (btn) {
    btn.setAttribute("data-count", count);
    btn.setAttribute("aria-label", `Cart (${count} ${count === 1 ? "item" : "items"})`);
  }
}

const dialogFocusable = (dialog) => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', dialog)
  .filter((el) => !el.hidden && el.getAttribute("aria-hidden") !== "true");
function focusDialog(dialog) {
  const moveFocus = () => {
    if (!dialog.classList.contains("open")) return;
    const target = dialog.querySelector("[data-dialog-close]") || dialogFocusable(dialog)[0] || dialog;
    if (document.activeElement !== target) target.focus({ preventScroll: true });
  };
  window.requestAnimationFrame(() => {
    moveFocus();
    window.setTimeout(moveFocus, 80);
  });
}
function trapDialogKeydown(event, dialog, close) {
  if (event.key === "Escape") { event.preventDefault(); close(); return; }
  if (event.key !== "Tab") return;
  const focusable = dialogFocusable(dialog);
  if (!focusable.length) { event.preventDefault(); dialog.focus(); return; }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const outside = !dialog.contains(document.activeElement);
  if (event.shiftKey && (outside || document.activeElement === first)) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && (outside || document.activeElement === last)) { event.preventDefault(); first.focus(); }
}

let cartOpener = null;

async function openCart() {
  if (!$("#drawer")?.classList.contains("open") && document.activeElement instanceof HTMLElement) cartOpener = document.activeElement;
  lockStorefrontScroll();
  $("#scrim")?.classList.add("open");
  const dr = $("#drawer");
  const scrim = $("#scrim");
  if (dr) dr.inert = false;
  if (dr) isolateStorefrontOverlay(dr, scrim);
  dr?.classList.add("open", "drawer--entering");
  dr?.setAttribute("aria-hidden", "false");
  $("#cartBtn")?.setAttribute("aria-expanded", "true");
  setTimeout(() => dr?.classList.remove("drawer--entering"), 750);
  await renderCart();
  if (dr?.classList.contains("open")) focusDialog(dr);
}

function closeCart() {
  $("#scrim")?.classList.remove("open");
  const drawer = $("#drawer");
  drawer?.classList.remove("open");
  drawer?.setAttribute("aria-hidden", "true");
  if (drawer) drawer.inert = true;
  $("#cartBtn")?.setAttribute("aria-expanded", "false");
  restoreStorefrontIsolation();
  window.setTimeout(unlockStorefrontScroll, 300);
  if (cartOpener?.isConnected) cartOpener.focus();
  cartOpener = null;
}

function vividArtFor(item) {
  if (item.image) return item.image;
  try {
    const map = JSON.parse(document.getElementById("vividArtMap")?.textContent || "{}");
    const handle = (item.url || "").split("/products/")[1]?.split("?")[0]?.replace(/\/$/, "") || "";
    return map[handle] || "";
  } catch (e) { return ""; }
}

async function renderCart() {
  await olMapReady;
  let cart;
  try {
    cart = await cartFetch();
  } catch (_error) {
    const body = $("#drawerBody");
    const foot = $("#drawerFoot");
    if (body) body.innerHTML = '<div class="cart-empty"><p>We could not load your basket. Check your connection and try again.</p><button type="button" class="btn btn--ghost" style="margin-top:18px" onclick="renderCart()">Try again</button></div>';
    if (foot) foot.innerHTML = "";
    return null;
  }
  setPip(cart.item_count);
  const body = $("#drawerBody");
  const foot = $("#drawerFoot");
  if (!body) return;
  if (cart.item_count === 0) {
    body.innerHTML =
      '<div class="cart-empty"><p>Your basket is empty.</p><a href="/collections/all-formulations" class="btn btn--ghost" style="margin-top:18px" onclick="closeCart()">Browse the range</a></div>';
    if (foot) foot.innerHTML = "";
    return;
  }
  const ho = olHandoff(cart);
  const holdWhy = {};
  ho.held.forEach((h) => { holdWhy[h.item.key] = h.why; });
  const olLine = {};
  ho.carried.forEach((c) => { olLine[c.item.key] = c.m.p * c.item.quantity; });
  body.innerHTML = cart.items
    .map(
      (i) => `
    <div class="cart-item${holdWhy[i.key] ? " cart-item--held" : ""}">
      <div class="cart-item-img">${vividArtFor(i) ? `<img src="${vividArtFor(i)}" alt="" loading="lazy">` : ""}</div>
      <div>
        <div class="cart-item-name">${i.product_title}</div>
        <div class="cart-item-meta">${i.variant_title || ""}</div>
        ${holdWhy[i.key] ? `<div class="cart-item-hold">${holdWhy[i.key]}</div>` : ""}
        <div style="display:flex;gap:8px;margin-top:8px;align-items:center">
          <div class="qty" style="height:30px">
            <button type="button" onclick="cartLineChange('${i.key}', ${i.quantity - 1})" aria-label="Decrease">−</button>
            <input value="${i.quantity}" readonly aria-label="Quantity">
            <button type="button" onclick="cartLineChange('${i.key}', ${i.quantity + 1})" aria-label="Increase">+</button>
          </div>
          <button type="button" class="cart-item-remove" onclick="cartLineChange('${i.key}', 0)" aria-label="Remove ${i.product_title}">Remove</button>
        </div>
      </div>
      <div class="cart-item-price">${formatR(olLine[i.key] != null ? olLine[i.key] : i.final_line_price)}</div>
    </div>`
    )
    .join("");
  body.innerHTML += '<div id="cartUpsell" class="cart-upsell"></div>';
  renderCartUpsell(cart);
  const payable = ho.url ? ho.total : cart.total_price;
  const fillPct = Math.min(100, (payable / 40000) * 100);
  const togo = Math.max(0, 40000 - payable);
  const shipMsg =
    togo === 0
      ? `<div class="ship-msg"><strong>You've unlocked free shipping.</strong> Nice.</div>`
      : `<div class="ship-msg">Spend <strong>${formatR(togo)}</strong> more for free shipping</div>`;
  if (foot) {
    const heldWhys = ho.held.map((h) => h.why).filter((v, i, a) => a.indexOf(v) === i).join("; ");
    const heldNote = ho.held.length
      ? `<div class="cart-totals-note cart-totals-note--held">${ho.held.length === 1 ? "One formula waits in your basket" : ho.held.length + " formulas wait in your basket"} \u2014 ${heldWhys}.</div>`
      : "";
    const checkoutBlock = ho.url
      ? `<div class="ol-handoff"><span class="ol-handoff-dot"></span><em>Checks out securely at</em><b>One Life Health</b></div>
      <a class="btn btn--forest btn--block cart-checkout-btn" href="${ho.url}">Checkout \u2014 ${formatR(ho.total)}</a>`
      : `<div class="ol-handoff"><span class="ol-handoff-dot"></span><em>Ordering opens soon at</em><b>One Life Health</b></div>
      <a class="btn btn--ghost btn--block cart-checkout-btn" href="${OL_WHATSAPP(cart.items)}" target="_blank" rel="noopener">Order via WhatsApp</a>`;
    foot.innerHTML = `
      <div class="ship-bar"><div class="ship-bar-fill" style="width:${fillPct}%"></div></div>
      ${shipMsg}
      <div class="cart-totals total"><span>Subtotal</span><span>${formatR(payable)}</span></div>
      <div class="cart-totals-note">${payable >= 40000 ? "Free nationwide delivery applied at checkout." : "Delivery calculated at checkout \u00b7 free over R400."}</div>
      ${heldNote}
      <div class="cart-totals-note cart-totals-note--pkg">Our packaging is getting a design refresh \u2014 bottles received may look slightly different from the photos.</div>
      ${checkoutBlock}
    `;
  }
}

/* Cart-drawer qty/remove buttons route through here so a failed change
   (stock limit, since-sold-out variant) surfaces a notice instead of just
   silently re-rendering whatever the cart actually contains. */
const cartLineBusy = new Set();
async function cartLineChange(key, qty) {
  if (cartLineBusy.has(key)) return;
  cartLineBusy.add(key);
  document.querySelectorAll(".cart-item button").forEach((button) => { button.disabled = true; });
  try {
    const res = await cartChange(key, qty);
    await renderCart();
    if (!res._ok) showCartNotice(res.description || res.message || "That change couldn't be made — please refresh and try again.");
  } finally {
    cartLineBusy.delete(key);
  }
}

/* ─── Sticky mobile ATC / CTA ─── */

let stickyEl = null;
let stickyObserver = null;

function clearSticky() {
  if (stickyObserver) { stickyObserver.disconnect(); stickyObserver = null; }
  if (stickyEl) { stickyEl.remove(); stickyEl = null; }
}

/* Product-context sticky (PDP). Variant id + price + Add button. */
function initStickyProduct({ name, price, variantId, inStock }) {
  clearSticky();
  const el = document.createElement("div");
  el.className = "sticky-atc";
  el.innerHTML = `
    <span class="name">${name}</span>
    <span class="price">${price}</span>
    <button class="btn btn--forest" type="button" ${inStock ? "" : "disabled"} data-variant-id="${variantId}" data-action="sticky-add">
      ${inStock ? "Add" : "Sold out"}
    </button>
  `;
  document.body.appendChild(el);
  stickyEl = el;
  const addRow = document.querySelector(".add-row");
  if (!addRow) return;
  const footer = document.querySelector("footer");
  let triggerVisible = true;
  let footerVisible = false;
  stickyObserver = new IntersectionObserver((entries) => {
    if (stickyEl !== el || !el.isConnected) return;
    entries.forEach((entry) => {
      if (entry.target === addRow) triggerVisible = entry.isIntersecting;
      if (entry.target === footer) footerVisible = entry.isIntersecting;
    });
    el.classList.toggle("visible", !triggerVisible && !footerVisible);
  }, { threshold: 0 });
  stickyObserver.observe(addRow);
  if (footer) stickyObserver.observe(footer);
}

/* CTA-context sticky (home, journal, about, etc.). Mirrors hero CTAs. */
function initStickyCTA(triggerSelector, ctas) {
  clearSticky();
  const el = document.createElement("div");
  el.className = "sticky-atc sticky-atc--cta";
  el.innerHTML = ctas
    .map((c) => `<a href="${c.href}" class="btn ${c.cls || ""}">${c.label}</a>`)
    .join("");
  document.body.appendChild(el);
  stickyEl = el;
  const trigger = document.querySelector(triggerSelector);
  if (!trigger) return;
  const footer = document.querySelector("footer");
  let triggerVisible = true;
  let footerVisible = false;
  stickyObserver = new IntersectionObserver((entries) => {
    if (stickyEl !== el || !el.isConnected) return;
    entries.forEach((entry) => {
      if (entry.target === trigger) triggerVisible = entry.isIntersecting;
      if (entry.target === footer) footerVisible = entry.isIntersecting;
    });
    el.classList.toggle("visible", !triggerVisible && !footerVisible);
  }, { threshold: 0 });
  stickyObserver.observe(trigger);
  if (footer) stickyObserver.observe(footer);
}

/* facelift 2026-07: every main-content section joins the reveal
   choreography (class added by JS only, so no-JS renders static) */
document.querySelectorAll("#MainContent .shopify-section, main .shopify-section").forEach(function (sec) {
  if (!sec.querySelector(".reveal")) sec.classList.add("reveal");
});

/* ─── Reveal-on-scroll for .reveal sections ─── */

function initReveal() {
  if (!("IntersectionObserver" in window)) {
    $$(".reveal").forEach((el) => el.classList.add("in"));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
    });
    /* threshold 0: a ratio-based threshold (e.g. 0.12) can never be reached
       by a section taller than viewport-height / threshold — a bug that
       left the whole collection grid (250 unpaginated products) at
       opacity:0 forever. Any-pixel-visible + the bottom rootMargin is
       still a natural "scrolled into view" trigger for normal-sized
       sections, and now also works for arbitrarily tall ones. */
  }, { threshold: 0, rootMargin: "0px 0px -10% 0px" });
  $$(".reveal").forEach((el) => io.observe(el));
}

/* ─── Floating consultation chat ─── */

function escapeHTML(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openChat() {
  const widget = $("#chatWidget");
  const btn = $("#chatBtn");
  if (!widget) return;
  widget.classList.add("open");
  widget.setAttribute("aria-hidden", "false");
  btn?.setAttribute("aria-expanded", "true");
  $("#chatInput")?.focus();
}

function closeChat() {
  const widget = $("#chatWidget");
  const btn = $("#chatBtn");
  if (!widget) return;
  widget.classList.remove("open");
  widget.setAttribute("aria-hidden", "true");
  btn?.setAttribute("aria-expanded", "false");
}

function consultationReply(message) {
  const term = String(message || "").toLowerCase();
  if (term.includes("stack") || term.includes("routine") || term.includes("combine")) {
    return 'Start with one outcome. The <a href="/collections/bundles">curated stacks</a> are the cleanest path: Allergy, Bone & Joint, or Rest & Focus, each with a 10% stack offer routed at checkout.';
  }
  if (term.includes("allergy") || term.includes("hay") || term.includes("sinus")) {
    return 'For seasonal support, start with the <a href="/collections/immunity">Immunity range</a> or go straight to the Allergy Stack. If you use medication or have severe reactions, <a href="https://wa.me/27713744910" target="_blank" rel="noopener">WhatsApp a consultant</a> before combining products.';
  }
  if (term.includes("sleep") || term.includes("stress") || term.includes("calm") || term.includes("mood")) {
    return 'For evenings, look at <a href="/collections/stress">Stress, Sleep & Mood</a>. If you want the routine built for you, the Rest & Focus Stack is the more complete starting point.';
  }
  if (term.includes("joint") || term.includes("bone") || term.includes("mobility") || term.includes("pain")) {
    return 'For structure and movement, start with <a href="/collections/joints">Joints & Mobility</a>. Bone Supreme is the foundation; the Bone & Joint Stack is the fuller mobility routine.';
  }
  if (term.includes("gut") || term.includes("digest") || term.includes("cleanse")) {
    return 'For digestion, use the <a href="/collections/gut">Gut Health range</a>. If you are unsure whether you need cleanse, lining support, or daily balance, the quiz will narrow that down.';
  }
  if (term.includes("quiz") || term.includes("recommend") || term.includes("choose")) {
    return 'Use the <a href="/pages/quiz">premium formulation quiz</a>. It starts with your goal, routine style, and format preference so you do not have to browse 52 products cold.';
  }
  if (term.includes("consult") || term.includes("human") || term.includes("whatsapp")) {
    return 'For personal suitability, dosage, or supplement-combination questions, <a href="https://wa.me/27713744910" target="_blank" rel="noopener">WhatsApp a Vivid consultant</a>. It is the best route for anything specific to your situation.';
  }
  return 'The fastest route is the <a href="/pages/quiz">premium quiz</a>. For a human check before you combine products, <a href="https://wa.me/27713744910" target="_blank" rel="noopener">WhatsApp a consultant</a>. For pre-built routines, shop <a href="/collections/bundles">curated stacks</a>.';
}

function sendChat(input) {
  const field = input || $("#chatInput");
  const body = $("#chatBody");
  if (!field || !body) return;
  const msg = field.value.trim();
  if (!msg) return;
  field.value = "";
  body.querySelector(".chat-suggestions")?.remove();
  body.insertAdjacentHTML("beforeend", `<div class="chat-msg chat-msg--me"><p>${escapeHTML(msg)}</p></div>`);
  body.insertAdjacentHTML("beforeend", '<div class="chat-typing" id="chatTyping"><span></span><span></span><span></span></div>');
  body.scrollTop = body.scrollHeight;
  window.setTimeout(() => {
    $("#chatTyping")?.remove();
    body.insertAdjacentHTML("beforeend", `<div class="chat-msg chat-msg--them"><p>${consultationReply(msg)}</p><span class="chat-time">Vivid Health</span></div>`);
    body.scrollTop = body.scrollHeight;
  }, 650);
}

function initFloatingChat() {
  $("#chatBtn")?.addEventListener("click", openChat);
  $("#chatCloseBtn")?.addEventListener("click", closeChat);
  $("#chatSendBtn")?.addEventListener("click", () => sendChat($("#chatInput")));
  $("#chatInput")?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") sendChat(e.currentTarget);
  });
  $$(".chat-suggestions button").forEach((button) => {
    button.addEventListener("click", () => {
      const input = $("#chatInput");
      if (!input) return;
      input.value = button.dataset.chatPrompt || button.textContent || "";
      sendChat(input);
    });
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && $("#chatWidget")?.classList.contains("open")) closeChat();
  });
}

/* ─── Premium quiz page ─── */

function initPremiumQuiz() {
  const root = $("[data-vivid-quiz]");
  if (!root) return;
  const title = $("[data-quiz-result-title]", root);
  const desc = $("[data-quiz-result-desc]", root);
  const link = $("[data-quiz-result-link]", root);
  const detailLink = $("[data-quiz-stack-link]", root);
  const summary = $("[data-quiz-summary]", root);
  const primary = $("[data-quiz-primary]", root);
  const reasons = $("[data-quiz-reasons]", root);
  const plan = $("[data-quiz-plan]", root);
  const products = $("[data-quiz-products]", root);
  const whatsapp = $(".quiz-whatsapp", root);

  const labels = {
    goal: {
      gut: "Gut health",
      immunity: "Immunity",
      stress: "Sleep & stress",
      joints: "Joints & mobility",
      daily: "Daily nutrients",
      energy: "Energy",
      women: "Women's health",
      men: "Men's health",
    },
    signal: {
      cleanse: "Reset or cleanse",
      lining: "Daily gut repair",
      seasonal: "Seasonal flare-ups",
      "daily-immune": "Daily defence",
      sleep: "Restless nights",
      mood: "Mood and stress load",
      bone: "Bone structure",
      mobility: "Joint comfort",
      cellular: "Cellular energy",
      foundation: "Daily foundation",
      cycle: "Cycle or hormones",
      prostate: "Prostate support",
    },
    style: {
      single: "One clear bottle",
      pair: "Two-product route",
      stack: "Complete stack",
    },
    format: {
      capsule: "Capsules",
      powder: "Powders",
      open: "No preference",
    },
  };

  const catalog = [
    { type: "product", name: "Colon Flush", range: "Gut Health", goals: ["gut"], signals: ["cleanse"], format: "capsule", url: "/products/vivid-health-gut-health-colon-flush-120-capsules", price: "R196.07", form: "120 capsules", why: "Capsule cleanse support when the goal is a defined gut reset.", timing: "Use as a short, structured reset route.", intensity: "active" },
    { type: "product", name: "Colon Flush Powder", range: "Gut Health", goals: ["gut"], signals: ["cleanse"], format: "powder", url: "/products/vivid-health-gut-health-colon-flush-powder-135g", price: "R143.00", form: "135g", why: "Powder format gives more flexible cleanse dosing.", timing: "Best when you prefer water-mix routines.", intensity: "active" },
    { type: "product", name: "L-Glutamine", range: "Gut Health", goals: ["gut"], signals: ["lining"], format: "powder", url: "/products/vivid-health-physical-health-l-glutamine-500g", price: "R345.00", form: "500g powder", why: "A cleaner first step for gut lining and recovery routines.", timing: "Use daily before adding cleanse botanicals.", intensity: "gentle" },
    { type: "product", name: "Wormwood", range: "Gut Health", goals: ["gut"], signals: ["cleanse"], format: "capsule", url: "/products/vivid-health-gut-health-immune-wormwood-60-capsules", price: "R133.00", form: "60 capsules", why: "Botanical gut support for a more targeted cleanse route.", timing: "Use after the anchor product if the reset needs support.", intensity: "active" },
    { type: "product", name: "Black Walnut", range: "Gut Health", goals: ["gut"], signals: ["cleanse"], format: "capsule", url: "/products/vivid-health-gut-health-black-walnut-60-capsules", price: "R140.41", form: "60 capsules", why: "A targeted add-on for cleanse-led gut routines.", timing: "Add only when the routine is already clear.", intensity: "active" },

    { type: "product", name: "Allergy Control", range: "Immune Health", goals: ["immunity"], signals: ["seasonal"], format: "capsule", url: "/products/vivid-health-immune-allergy-control-60-capsules", price: "R199.00", form: "60 capsules", why: "The cleanest anchor when the real issue is seasonal allergy or histamine support.", timing: "Use as the first seasonal-support product.", intensity: "gentle" },
    { type: "product", name: "Quercetin Complex", range: "Immune Health", goals: ["immunity"], signals: ["seasonal"], format: "capsule", url: "/products/vivid-health-immune-quercetin-complex-60-capsules", price: "R285.20", form: "60 capsules", why: "A logical second product for flavonoid-based seasonal support.", timing: "Add if Allergy Control alone is not enough.", intensity: "support" },
    { type: "product", name: "Buffered C", range: "Immune Health", goals: ["immunity", "daily"], signals: ["daily-immune", "foundation", "seasonal"], format: "capsule", url: "/products/vivid-health-immune-buffered-c-90-capsules", price: "R189.76", form: "90 capsules", why: "A simple daily immune foundation without a complicated routine.", timing: "Use daily as a low-friction base.", intensity: "gentle" },
    { type: "product", name: "Buffered C Powder", range: "Immune Health", goals: ["immunity", "daily"], signals: ["daily-immune", "foundation"], format: "powder", url: "/products/vivid-health-immune-buffered-c-150g", price: "R105.34", form: "150g", why: "Powder-friendly immune support for customers who dislike capsules.", timing: "Mix into water when a capsule routine will not stick.", intensity: "gentle" },
    { type: "product", name: "Immune Plus", range: "Immune Health", goals: ["immunity"], signals: ["daily-immune"], format: "capsule", url: "/products/vivid-health-immune-immune-plus-60-capsules", price: "R125.00", form: "60 capsules", why: "A concise immune-complex option when the goal is daily defence.", timing: "Use as a simple immune starting point.", intensity: "support" },
    { type: "product", name: "Mullein", range: "Immune Health", goals: ["immunity"], signals: ["seasonal", "daily-immune"], format: "capsule", url: "/products/vivid-health-immune-mullein-60-capsules", price: "R133.00", form: "60 capsules", why: "Better fit when seasonal support leans respiratory.", timing: "Use as a targeted add-on, not a random extra.", intensity: "support" },

    { type: "product", name: "Griffonia (5-HTP)", range: "Stress, Sleep & Mood", goals: ["stress"], signals: ["sleep", "mood"], format: "capsule", url: "/products/vivid-health-stay-vivid-griffonia-5-htp-60-capsules", price: "R275.00", form: "60 capsules", why: "The anchor product when sleep, mood, and evening calm are connected.", timing: "Start in the evening routine before adding daytime support.", intensity: "support" },
    { type: "product", name: "Tranquil", range: "Stress, Sleep & Mood", goals: ["stress"], signals: ["sleep"], format: "capsule", url: "/products/vivid-health-stay-vivid-tranquil-60-capsules", price: "R130.00", form: "60 capsules", why: "Best fit when the main gap is wind-down and sleep rhythm.", timing: "Use as the evening calm product.", intensity: "support" },
    { type: "product", name: "GABA Powder", range: "Stress, Sleep & Mood", goals: ["stress"], signals: ["sleep", "mood"], format: "powder", url: "/products/vivid-health-stay-vivid-gaba-150g", price: "R258.00", form: "150g", why: "Powder format for customers who want flexible evening support.", timing: "Use as a controlled wind-down add-on.", intensity: "active" },
    { type: "product", name: "Liquorice Root", range: "Stress, Sleep & Mood", goals: ["stress", "gut"], signals: ["mood", "lining"], format: "capsule", url: "/products/vivid-health-stay-vivid-liquorice-root-60-capsules", price: "R132.99", form: "60 capsules", why: "A better fit when stress support overlaps with gut comfort.", timing: "Use as a targeted bridge product.", intensity: "support" },

    { type: "product", name: "Bone Supreme", range: "Joints & Mobility", goals: ["joints", "daily"], signals: ["bone"], format: "capsule", url: "/products/vivid-health-vivid-body-bone-supreme-120-capsules", price: "R437.00", form: "120 capsules", why: "The structural anchor when bone matrix and long-term mineral support are the priority.", timing: "Use as the foundation before adding mobility support.", intensity: "gentle" },
    { type: "product", name: "Flexijoint Advanced", range: "Joints & Mobility", goals: ["joints"], signals: ["mobility"], format: "capsule", url: "/products/vivid-health-vivid-body-flexijoint-advanced-120-capsules", price: "R322.00", form: "120 capsules", why: "The better match when the customer is focused on joint movement and comfort.", timing: "Use when mobility is the daily gap.", intensity: "support" },
    { type: "product", name: "MSM", range: "Joints & Mobility", goals: ["joints"], signals: ["mobility"], format: "capsule", url: "/products/vivid-health-vivid-body-msm-90-capsules", price: "R170.78", form: "90 capsules", why: "A clean connective-tissue add-on for joint and skin routines.", timing: "Add after the anchor product if movement support needs depth.", intensity: "support" },
    { type: "product", name: "MSM Powder", range: "Joints & Mobility", goals: ["joints"], signals: ["mobility"], format: "powder", url: "/products/vivid-health-vivid-body-msm-150g", price: "R136.85", form: "150g", why: "Powder option for people who prefer flexible joint-support dosing.", timing: "Use when capsules are not the preferred format.", intensity: "support" },
    { type: "product", name: "Joint Relief", range: "Joints & Mobility", goals: ["joints"], signals: ["mobility"], format: "capsule", url: "/products/vivid-health-vivid-body-joint-relief-60-capsules", price: "R138.00", form: "60 capsules", why: "A simple joint-comfort product when the goal is daily movement ease.", timing: "Use as the conservative mobility start.", intensity: "gentle" },

    { type: "product", name: "D-Ribose", range: "Energy & Vitality", goals: ["energy"], signals: ["cellular"], format: "powder", url: "/products/vivid-health-stay-vivid-d-ribose-150g", price: "R307.39", form: "150g powder", why: "The strongest fit when the brief is cellular fuel rather than stimulants.", timing: "Use around the routine where energy demand is highest.", intensity: "support" },
    { type: "product", name: "Coenzyme Q10", range: "Energy & Vitality", goals: ["energy", "daily"], signals: ["cellular", "foundation"], format: "capsule", url: "/products/vivid-health-vivid-body-coenzyme-q10-60-capsules", price: "R340.40", form: "60 capsules", why: "Capsule-based mitochondrial and heart-support route.", timing: "Use daily when capsule convenience matters.", intensity: "gentle" },
    { type: "product", name: "Moringa", range: "Daily Nutrients", goals: ["daily", "energy"], signals: ["foundation", "cellular"], format: "capsule", url: "/products/vivid-health-vivid-nourishment-moringa-powder-300-capsules", price: "R298.08", form: "300 capsules", why: "A broader daily-nutrition product when energy may be nutrient-linked.", timing: "Use as an everyday shelf product.", intensity: "gentle" },

    { type: "product", name: "Omega Oil", range: "Daily Nutrients", goals: ["daily", "joints"], signals: ["foundation", "mobility"], format: "capsule", url: "/products/vivid-health-physical-health-omega-oil-90-capsules", price: "R237.18", form: "90 capsules", why: "A clean daily foundation before more specialised support.", timing: "Use daily as a low-friction nutritional base.", intensity: "gentle" },
    { type: "product", name: "Barley Grass Powder", range: "Daily Nutrients", goals: ["daily"], signals: ["foundation"], format: "powder", url: "/products/vivid-health-nutritent-health-barley-grass-200g", price: "R224.25", form: "200g", why: "Greens support in a flexible powder format.", timing: "Use in a smoothie or water routine.", intensity: "gentle" },
    { type: "product", name: "Barley Grass", range: "Daily Nutrients", goals: ["daily"], signals: ["foundation"], format: "capsule", url: "/products/vivid-health-nutritent-health-barley-grass-300-capsules", price: "R283.02", form: "300 capsules", why: "Greens support without needing a powder routine.", timing: "Use daily when capsule consistency matters.", intensity: "gentle" },
    { type: "product", name: "Turmeric Plus", range: "Daily Nutrients", goals: ["daily", "joints"], signals: ["foundation", "mobility"], format: "capsule", url: "/products/vivid-health-nutrient-health-turmeric-plus-60-capsules", price: "R257.60", form: "60 capsules", why: "Daily botanical support when the foundation also needs joint relevance.", timing: "Use as a simple botanical base.", intensity: "support" },

    { type: "product", name: "Agnus Castus", range: "Women's Health", goals: ["women"], signals: ["cycle"], format: "capsule", url: "/products/vivid-health-woman-angus-castus-60-capsules", price: "R180.00", form: "60 capsules", why: "Best match when cycle rhythm or PMS is the main question.", timing: "Use as the first women's health anchor.", intensity: "support" },
    { type: "product", name: "Sage", range: "Women's Health", goals: ["women"], signals: ["cycle"], format: "capsule", url: "/products/vivid-health-woman-sage-60-capsules", price: "R120.61", form: "60 capsules", why: "A better fit when the life-stage concern is hot flush or menopause support.", timing: "Use as a targeted life-stage product.", intensity: "support" },
    { type: "product", name: "Maca", range: "Women's Health", goals: ["women", "energy"], signals: ["cycle", "cellular"], format: "capsule", url: "/products/vivid-health-woman-maca-60-capsules", price: "R179.86", form: "60 capsules", why: "A broader endocrine-adaptogen route when hormones overlap with vitality.", timing: "Use when balance and energy are both relevant.", intensity: "support" },
    { type: "product", name: "Prosta Care", range: "Men's Health", goals: ["men"], signals: ["prostate"], format: "capsule", url: "/products/vivid-health-men-prosta-care-60-capsules", price: "R263.93", form: "60 capsules", why: "The direct men's health match for prostate and daily vitality support.", timing: "Use as the men's health anchor product.", intensity: "support" },

    { type: "stack", name: "Vivid Allergy Stack", range: "Immune Health", goals: ["immunity"], signals: ["seasonal"], format: "capsule", url: "/products/vivid-allergy-stack-complete-allergy-support-bundle", price: "R594.72", form: "3-product stack", why: "Allergy Control, Quercetin Complex, and Buffered C in one seasonal route.", timing: "Choose this when allergies keep returning and one bottle is not enough.", intensity: "stack" },
    { type: "stack", name: "Vivid Bone & Joint Pack", range: "Joints & Mobility", goals: ["joints"], signals: ["bone", "mobility"], format: "capsule", url: "/products/vivid-bone-joint-pack-complete-skeletal-support-bundle", price: "R742.66", form: "3-product stack", why: "Bone Supreme, MSM, and Flexijoint paired for structure and movement.", timing: "Choose this when bone, connective tissue, and mobility all matter.", intensity: "stack" },
    { type: "stack", name: "Vivid Rest & Focus Stack", range: "Stress, Sleep & Mood", goals: ["stress"], signals: ["sleep", "mood"], format: "capsule", url: "/products/vivid-rest-focus-stack-sleep-calm-and-mental-clarity-bundle", price: "R587.67", form: "3-product stack", why: "Griffonia, Tranquil, and GABA arranged into a calmer evening system.", timing: "Choose this when sleep, stress, and next-day focus are connected.", intensity: "stack" },
  ];

  const selected = (group) => $(`[data-quiz-choice="${group}"].selected`, root);
  const signalChoices = $$("[data-quiz-choice='signal']", root);
  const productCache = new Map();
  const imageMapEl = document.querySelector("[data-quiz-image-map]");
  const imageMap = (() => {
    try {
      return imageMapEl ? JSON.parse(imageMapEl.textContent || "{}") : {};
    } catch (_error) {
      return {};
    }
  })();
  const variantMap = {
    "vivid-health-gut-health-colon-flush-powder-135g": 44794457292886,
    "vivid-health-physical-health-l-glutamine-500g": 44794457456726,
    "vivid-health-gut-health-immune-wormwood-60-capsules": 44794455425110,
    "vivid-health-gut-health-black-walnut-60-capsules": 44794455392342,
    "vivid-health-immune-allergy-control-60-capsules": 44794457260118,
    "vivid-health-immune-quercetin-complex-60-capsules": 44794458308694,
    "vivid-health-immune-buffered-c-90-capsules": 44794457915478,
    "vivid-health-immune-buffered-c-150g": 44794457981014,
    "vivid-health-immune-immune-plus-60-capsules": 44794458472534,
    "vivid-health-immune-mullein-60-capsules": 44794455162966,
    "vivid-health-stay-vivid-griffonia-5-htp-60-capsules": 44794458505302,
    "vivid-health-stay-vivid-tranquil-60-capsules": 44794457555030,
    "vivid-health-stay-vivid-gaba-150g": 44794458538070,
    "vivid-health-stay-vivid-liquorice-root-60-capsules": 44794457751638,
    "vivid-health-vivid-body-msm-90-capsules": 44794457653334,
    "vivid-health-vivid-body-msm-150g": 44794458406998,
    "vivid-health-vivid-body-joint-relief-60-capsules": 44794457784406,
    "vivid-health-stay-vivid-d-ribose-150g": 44794455556182,
    "vivid-health-vivid-body-coenzyme-q10-60-capsules": 44794459062358,
    "vivid-health-vivid-nourishment-moringa-powder-300-capsules": 44794458439766,
    "vivid-health-physical-health-omega-oil-90-capsules": 44794456309846,
    "vivid-health-nutrient-health-turmeric-plus-60-capsules": 44794458046550,
    "vivid-health-woman-angus-castus-60-capsules": 44794459258966,
    "vivid-health-woman-sage-60-capsules": 44794457620566,
    "vivid-allergy-stack-complete-allergy-support-bundle": 44794645610582,
    "vivid-bone-joint-pack-complete-skeletal-support-bundle": 44794645577814,
    "vivid-rest-focus-stack-sleep-calm-and-mental-clarity-bundle": 44794645545046,
  };
  let resultToken = 0;

  const currentSelections = () => ({
    goal: selected("goal")?.dataset.goalKey || "gut",
    signal: selected("signal")?.dataset.signalKey || "cleanse",
    style: selected("style")?.dataset.styleKey || "single",
    format: selected("format")?.dataset.formatKey || "capsule",
  });

  const syncSignalChoices = () => {
    const activeGoal = selected("goal")?.dataset.goalKey || "gut";
    const visibleSignals = signalChoices.filter((choice) => {
      const goals = (choice.dataset.signalGoals || "").split(" ");
      const visible = goals.includes(activeGoal);
      choice.hidden = !visible;
      return visible;
    });
    const currentSignal = selected("signal");
    if (!currentSignal || currentSignal.hidden) {
      signalChoices.forEach((choice) => choice.classList.remove("selected"));
      visibleSignals[0]?.classList.add("selected");
    }
  };

  const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const normaliseImage = (url) => {
    if (!url) return "";
    if (url.startsWith("//")) return `https:${url}`;
    return url;
  };

  const productHandle = (product) => product?.url?.split("/products/")[1]?.split("?")[0]?.replace(/\/$/, "") || "";

  const hydrateProduct = (product) => {
    if (!product?.url?.includes("/products/")) return Promise.resolve(product);
    if (productCache.has(product.url)) return productCache.get(product.url);
    const handle = productHandle(product);
    const mappedImage = imageMap[handle];
    const fallbackVariantId = variantMap[handle];
    const fallbackCartUrl = fallbackVariantId
      ? `/cart/add?id=${fallbackVariantId}&quantity=1&return_to=/cart`
      : product.url;
    const promise = fetch(`${product.url.replace(/\/$/, "")}.js`)
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        const variants = data?.variants || [];
        const availableVariant = variants.find((variant) => variant.available) || variants[0];
        const variantId = availableVariant?.id || fallbackVariantId;
        const available = variants.length ? Boolean(availableVariant?.available) : Boolean(fallbackVariantId);
        const rawPrice = availableVariant?.price ?? data?.price;
        const livePriceCents = typeof rawPrice === "string" && rawPrice.includes(".")
          ? Math.round(Number(rawPrice) * 100)
          : Number(rawPrice);
        return {
          ...product,
          shopifyTitle: data?.title || product.name,
          price: Number.isFinite(livePriceCents) ? formatR(livePriceCents) : product.price,
          image: normaliseImage(mappedImage || data?.featured_image || data?.images?.[0] || product.image),
          variantId,
          available,
          cartUrl: available && variantId
            ? `/cart/add?id=${variantId}&quantity=1&return_to=/cart`
            : product.url,
        };
      })
      .catch(() => ({
        ...product,
        image: normaliseImage(mappedImage || product.image || ""),
        variantId: fallbackVariantId,
        available: Boolean(fallbackVariantId),
        cartUrl: fallbackCartUrl,
      }));
    productCache.set(product.url, promise);
    return promise;
  };

  const scoreProduct = (product, state) => {
    let score = 0;
    if (product.goals.includes(state.goal)) score += 12;
    if (product.signals.includes(state.signal)) score += 16;
    if (state.format === "open") score += 2;
    else if (product.format === state.format) score += 20;
    else score -= 18;
    if (state.style === "stack") score += product.type === "stack" ? 18 : 0;
    if (state.style === "pair") score += product.type === "product" ? 4 : 7;
    if (state.style === "single") score += product.type === "product" ? 8 : -12;
    if (product.type === "product") score += 1;
    return score;
  };

  const rankedCatalog = (state) => catalog
      .map((product) => ({ product, score: scoreProduct(product, state) }))
      .sort((a, b) => b.score - a.score)
      .map((item) => item.product);

  const directMatches = (items, state) => items.filter((product) => {
    if (!product.goals.includes(state.goal)) return false;
    if (state.format !== "open" && product.format !== state.format) return false;
    return true;
  });

  const preferredPrimary = (items, state) => {
    if (state.style === "stack" && state.format !== "powder") {
      const stack = items.find((product) => product.type === "stack");
      if (stack) return stack;
    }
    return items.find((product) => product.type === "product") || items[0];
  };

  const recommendationSet = async (state) => {
    const ranked = rankedCatalog(state);
    const hydrationPool = Array.from(new Set([
      ...directMatches(ranked, state),
      ...ranked.slice(0, 10),
    ]));
    const hydrated = await Promise.all(hydrationPool.map(hydrateProduct));
    const strict = directMatches(hydrated, state);
    const availableStrict = strict.filter((product) => product.available !== false);
    const availableHydrated = hydrated.filter((product) => product.available !== false);
    const candidatePool = availableStrict.length
      ? availableStrict
      : strict.length
        ? strict
        : availableHydrated.length
          ? availableHydrated
          : hydrated;
    const primaryProduct = preferredPrimary(candidatePool, state) || preferredPrimary(hydrated, state) || ranked[0];
    const supportMatches = (product) => {
      if (product.name === primaryProduct.name || product.type !== "product") return false;
      if (product.available === false) return false;
      if (product.goals.includes(state.goal)) return true;
      if (product.signals.includes(state.signal)) return true;
      return product.range === primaryProduct.range;
    };
    const supportSource = availableHydrated.length ? availableHydrated : hydrated;
    const supportLimit = state.style === "single" ? 0 : state.style === "pair" ? 1 : 3;
    const support = supportLimit
      ? supportSource.filter(supportMatches).slice(0, supportLimit)
      : [];
    return { primary: primaryProduct, support };
  };

  const renderSummary = (state) => {
    if (!summary) return;
    summary.innerHTML = "";
    [
      labels.goal[state.goal],
      labels.signal[state.signal],
      labels.style[state.style],
      labels.format[state.format],
    ].forEach((text) => {
      const pill = document.createElement("span");
      pill.textContent = text;
      summary.appendChild(pill);
    });
  };

  const renderPrimary = (product) => {
    if (!primary) return;
    primary.innerHTML = "";
    const card = document.createElement("a");
    card.className = "quiz-primary-card";
    card.href = product.available === false ? product.url : product.cartUrl;
    const image = product.image
      ? `<div class="quiz-primary-image"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.name)}"></div>`
      : "";
    card.innerHTML = `
      ${image}
      <span>${product.type === "stack" ? "Recommended stack" : "Best first product"}</span>
      <strong>${product.name}</strong>
      <em>${product.range} · ${product.form}</em>
      <b>${product.price}</b>
      <span class="quiz-card-cta">${product.available === false ? "View product" : "Add to cart"}</span>
      <small class="quiz-stock-note">${product.available === false ? "Currently unavailable - view product" : "Add directly to cart"}</small>
    `;
    primary.appendChild(card);
  };

  const renderReasons = (product, state) => {
    if (!reasons) return;
    reasons.innerHTML = "";
    const items = [
      `Matches your ${labels.goal[state.goal].toLowerCase()} goal.`,
      `Prioritises "${labels.signal[state.signal]}" instead of sending you to a generic collection.`,
      state.format === "open"
        ? "Format was left open, so the strongest product fit wins."
        : `Favours ${labels.format[state.format].toLowerCase()} where the range has a strong option.`,
      product.type === "stack"
        ? "You asked for a complete routine, so the stack is allowed to win."
        : "Starts with one anchor product before adding extras.",
    ];
    if (state.format !== "open" && product.format === state.format) {
      items.push(`Respects your ${labels.format[state.format].toLowerCase()} preference for the primary match.`);
    }
    items.forEach((text) => {
      const li = document.createElement("li");
      li.textContent = text;
      reasons.appendChild(li);
    });
  };

  const renderPlan = (product, support, state) => {
    if (!plan) return;
    plan.innerHTML = "";
    const steps = [];
    steps.push(`Start with ${product.name}. ${product.timing}`);
    if (state.style === "single") {
      steps.push("Keep it to one product for the first week so you know what is actually helping.");
    } else if (support[0]) {
      steps.push(`If the first product fits your day, add ${support[0].name} as the next logical support product.`);
    }
    if (state.style === "stack" && product.type === "stack") {
      steps.push("Use the stack only if you want the full routine from day one; otherwise start with the anchor product.");
    } else if (support[1]) {
      steps.push(`Keep ${support[1].name} as an optional later add-on, not an automatic purchase.`);
    }
    steps.forEach((text) => {
      const li = document.createElement("li");
      li.textContent = text;
      plan.appendChild(li);
    });
  };

  const renderProducts = (items) => {
    if (!products) return;
    const section = products.closest(".quiz-result-section");
    if (section) section.hidden = !(items || []).length;
    products.innerHTML = "";
    (items || []).forEach((product) => {
      const item = document.createElement("a");
      item.href = product.available === false ? product.url : product.cartUrl || product.url;
      item.className = "quiz-mini-product";
      const image = product.image
        ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.name)}">`
        : "";
      item.innerHTML = `
        ${image}
        <em>${product.type === "stack" ? "Stack" : product.range}</em>
        <strong>${product.name}</strong>
        <small>${product.form} · ${product.price}</small>
      `;
      products.appendChild(item);
    });
  };

  const updateResult = async () => {
    const token = ++resultToken;
    const state = currentSelections();
    renderSummary(state);
    if (title) title.textContent = "Building your match...";
    if (desc) desc.textContent = "Checking product format and current availability so the result can move straight to cart.";
    if (primary) {
      primary.innerHTML = `<div class="quiz-primary-card quiz-primary-card--loading"><span>Matching</span><strong>Checking availability</strong><p>We are finding the cleanest available product for your answers.</p></div>`;
    }
    const recs = await recommendationSet(state);
    if (token !== resultToken) return;
    const resultTitle = `${recs.primary.name} is your cleanest start`;
    const consultUrl = `https://wa.me/27713744910?text=${encodeURIComponent(`Hi, I took the Vivid quiz. My result is ${recs.primary.name}. Can you help me confirm this is the right fit?`)}`;
    const resultUrl = recs.primary.available === false ? recs.primary.url : recs.primary.cartUrl;
    if (title) title.textContent = resultTitle;
    if (desc) {
      desc.textContent = state.format !== "open" && recs.primary.format === state.format
        ? `Matched to your goal and ${labels.format[state.format].toLowerCase()} preference. Add it straight to cart or view the product first.`
        : "Matched to your goal and routine style. Add it straight to cart or view the product first.";
    }
    renderPrimary(recs.primary);
    renderReasons(recs.primary, state);
    renderPlan(recs.primary, recs.support, state);
    renderProducts(recs.support);
    if (link) {
      link.href = resultUrl;
      link.textContent = recs.primary.available === false ? `View ${recs.primary.name}` : `Add ${recs.primary.name} to cart`;
      link.removeAttribute("target");
      link.removeAttribute("rel");
    }
    if (detailLink) {
      detailLink.hidden = false;
      detailLink.href = recs.primary.url;
      detailLink.textContent = "View product details";
    }
    if (whatsapp) {
      whatsapp.href = consultUrl;
      whatsapp.textContent = "Ask a consultant before you combine products";
    }
  };

  root.addEventListener("click", (e) => {
    const choice = e.target.closest("[data-quiz-choice]");
    if (!choice) return;
    const group = choice.dataset.quizChoice;
    $$(`[data-quiz-choice="${group}"]`, root).forEach((item) => item.classList.remove("selected"));
    choice.classList.add("selected");
    if (group === "goal") syncSignalChoices();
    updateResult();
  });

  syncSignalChoices();
  updateResult();
}

/* ─── In-page commerce filters ─── */

function initHomeProductFilter() {
  const root = $("[data-home-product-filter]");
  if (!root) return;
  const heading = $("[data-home-products-heading]", root);
  const groups = $$("[data-home-group]", root);
  const buttons = $$("[data-home-filter]", root);

  const activate = (filter) => {
    const fallbackFilter = groups[0]?.dataset.homeGroup || buttons[0]?.dataset.homeFilter || "gut";
    const groupFilter = groups.some((group) => group.dataset.homeGroup === filter) ? filter : fallbackFilter;
    const selected = buttons.find((button) => button.dataset.homeFilter === groupFilter) || buttons[0];
    buttons.forEach((button) => {
      button.classList.toggle("is-active", button === selected);
      button.setAttribute("aria-pressed", button === selected ? "true" : "false");
    });
    groups.forEach((group) => {
      const active = group.dataset.homeGroup === groupFilter;
      group.hidden = !active;
      group.classList.toggle("is-active", active);
    });
    if (heading && selected?.dataset.heading) heading.textContent = selected.dataset.heading;
  };

  buttons.forEach((button) => {
    button.setAttribute("aria-pressed", button.classList.contains("is-active") ? "true" : "false");
    button.addEventListener("click", () => activate(button.dataset.homeFilter || "start"));
  });
}

function initCollectionFilters() {
  const root = $("[data-collection-filter]");
  const grid = $(".products-row--shop");
  if (!root || !grid) return;
  const cards = $$(".card", grid);
  const chips = $$("[data-filter-goal]", root);
  const count = $("[data-filter-count]");
  const sort = $("[data-filter-sort]", root);
  const initial = root.dataset.activeFilter || "all";
  const clientSideFiltering = root.dataset.clientFilter === "true";
  const collectionHead = $(".collection-head");
  const collectionCrumb = $(".collection-head .crumb");
  const collectionTitle = $(".collection-head h1");
  const collectionLead = $("[data-collection-lead]") || $(".collection-head p");
  const filterLabels = {
    all: "All formulations",
    bundles: "Curated stacks",
    gut: "Gut Health",
    immunity: "Immunity",
    stress: "Stress, Sleep & Mood",
    joints: "Joints & Mobility",
    energy: "Energy & Vitality",
    women: "Women's Health",
    men: "Men's Health",
    daily: "Daily Nutrients",
  };
  const filterLeads = {
    all: "Browse the complete Vivid Health range, then narrow by outcome without reloading the page.",
    bundles: "Pre-paired Vivid routines with a 10% stack offer.",
    gut: "Cleanse, digestive balance, lining support, and gut reset routines.",
    immunity: "Daily defence, allergy-season structure, and year-round immune support.",
    stress: "Calm, sleep, focus, and nervous-system support.",
    joints: "Bone, joint, connective-tissue, and mobility support.",
    energy: "Recovery, vitality, and daily fuel without stimulant theatre.",
    women: "Targeted support for women's daily rhythms.",
    men: "Targeted support for men's daily foundations.",
    daily: "The everyday wellness shelf: nutrients, greens, omegas, and foundations.",
  };

  const visibleCards = () => cards.filter((card) => !card.hidden);

  const applySort = () => {
    const value = sort?.value || "featured";
    const ordered = [...cards];
    if (value === "title-ascending") {
      ordered.sort((a, b) => (a.dataset.title || "").localeCompare(b.dataset.title || ""));
    } else if (value === "price-ascending") {
      ordered.sort((a, b) => Number(a.dataset.price || 0) - Number(b.dataset.price || 0));
    } else if (value === "price-descending") {
      ordered.sort((a, b) => Number(b.dataset.price || 0) - Number(a.dataset.price || 0));
    }
    ordered.forEach((card) => grid.appendChild(card));
  };

  const applyFilter = (filter) => {
    if (!filterLabels[filter]) filter = "all";
    let selectedChip = null;
    chips.forEach((chip) => {
      const active = chip.dataset.filterGoal === filter;
      chip.classList.toggle("is-active", active);
      chip.setAttribute("aria-pressed", active ? "true" : "false");
      if (active) selectedChip = chip;
    });
    cards.forEach((card) => {
      const goal = card.dataset.goal || "";
      const show = filter === "all" ? goal !== "bundles" : goal === filter;
      card.hidden = !show;
    });
    applySort();
    if (count) count.textContent = String(visibleCards().length);
    if (collectionTitle) collectionTitle.textContent = filterLabels[filter];
    if (collectionLead && filterLeads[filter]) collectionLead.textContent = filterLeads[filter];
    if (collectionCrumb) {
      const homeLink = collectionCrumb.querySelector("a")?.outerHTML || '<a href="/">Home</a>';
      collectionCrumb.innerHTML = `${homeLink} · ${filterLabels[filter]}`;
    }
    if (collectionHead && selectedChip?.dataset.filterBanner) {
      collectionHead.dataset.activeFilter = filter;
      collectionHead.style.setProperty("--banner", `url('${selectedChip.dataset.filterBanner}')`);
    }
  };

  chips.forEach((chip) => {
    if (!clientSideFiltering) return;
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-pressed", chip.classList.contains("is-active") ? "true" : "false");
    chip.addEventListener("click", (e) => {
      e.preventDefault();
      applyFilter(chip.dataset.filterGoal || "all");
    });
  });
  sort?.addEventListener("change", applySort);
  if (clientSideFiltering) {
    applyFilter(window.location.hash?.replace("#", "") || initial);
  } else {
    applySort();
    if (count) count.textContent = String(cards.length);
  }
}

/* ─── Boot ─── */

/* PDP fast-checkout link: one bottle, straight into the One Life checkout.
   Hidden when the formula has no live One Life listing — nothing sells here. */
function syncBuyNowToOL(bn) {
  if (!olMapFresh) { bn.hidden = true; return; }
  const handle = (location.pathname.split("/products/")[1] || "").split("?")[0].replace(/\/$/, "");
  const m = OL_MAP[handle];
  if (m && m.a) { bn.href = OL_BASE + "/cart/" + m.v + ":1"; bn.hidden = false; }
  else { bn.hidden = true; }
}

/* Cart page: the checkout submit becomes the One Life handoff. */
function initCartPageHandoff() {
  const form = document.querySelector(".cart-page form.cart-page-grid");
  if (!form) return;
  const btn = form.querySelector('button[name="checkout"]');
  if (!btn) return;
  const chip = document.createElement("div");
  chip.className = "ol-handoff";
  chip.innerHTML = '<span class="ol-handoff-dot"></span><em>Checks out securely at</em><b>One Life Health</b>';
  btn.parentNode.insertBefore(chip, btn);
  btn.addEventListener("click", async (e) => {
    e.preventDefault();
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Checking live price and stock…";
    try {
      await olMapReady;
      const cart = await cartFetch();
      const ho = olHandoff(cart);
      location.href = ho.url || OL_WHATSAPP(cart.items);
    } catch (_error) {
      btn.disabled = false;
      btn.textContent = "Could not connect — try again";
      window.setTimeout(() => { btn.textContent = original; }, 3000);
    }
  });
}

document.addEventListener("DOMContentLoaded", async () => {
  initReveal();
  initFloatingChat();
  initPremiumQuiz();
  initHomeProductFilter();
  initCollectionFilters();
  initSubToggle();
  initMobileNav();
  initCartPageHandoff();
  const bn0 = $(".pdp-buy-now");
  if (bn0) syncBuyNowToOL(bn0);

  /* PDP: sticky mobile Add-to-Cart bar once the buybox scrolls out of view */
  const pdpAddBtn = $(".pdp-buybox .js-add-to-cart");
  if (pdpAddBtn) {
    initStickyProduct({
      name: $("h1")?.textContent.trim() || "",
      price: $(".pdp-price")?.textContent.trim() || "",
      variantId: pdpAddBtn.dataset.variantId,
      inStock: !pdpAddBtn.disabled,
    });
  }

  /* Cart open */
  $("#cartBtn")?.addEventListener("click", openCart);

  /* PDP: add-to-cart from .js-add-to-cart, quantity stepper, tab switching, thumb switching */
  $$(".js-add-to-cart").forEach((b) =>
    b.addEventListener("click", async (e) => {
      e.preventDefault();
      if (b.dataset.busy === "1") return;
      b.dataset.busy = "1";
      try {
        const id = b.dataset.variantId;
        const qty = parseInt($("#qty")?.value || 1, 10);
        const sp = selectedSellingPlan(b);
        const res = await cartAdd(id, qty, sp);
        await openCart();
        if (!res._ok) showCartNotice(res.description || res.message || "Couldn't add that to your basket — please check stock and try again.");
      } finally {
        delete b.dataset.busy;
      }
    })
  );

  /* PDP: variant pills — sync price, add-to-cart target, and buy-now link */
  $$(".opt[data-variant-id]").forEach((b) =>
    b.addEventListener("click", () => {
      $$(".opt[data-variant-id]").forEach((x) => { x.classList.remove("active"); x.setAttribute("aria-pressed", "false"); });
      b.classList.add("active");
      b.setAttribute("aria-pressed", "true");
      const price = b.dataset.price;
      const vid = b.dataset.variantId;
      const available = b.dataset.available !== "false";
      const priceEl = $(".pdp-price");
      if (priceEl && price) priceEl.textContent = price;
      const atc = $(".js-add-to-cart");
      if (atc) {
        atc.dataset.variantId = vid;
        atc.disabled = !available;
        atc.textContent = available ? `Add to basket — ${price}` : "Notify me when back in stock";
      }
      const stickyAdd = document.querySelector('[data-action="sticky-add"]');
      const stickyPrice = document.querySelector(".sticky-atc .price");
      if (stickyAdd) {
        stickyAdd.dataset.variantId = vid;
        stickyAdd.disabled = !available;
        stickyAdd.textContent = available ? "Add" : "Sold out";
      }
      if (stickyPrice && price) stickyPrice.textContent = price;
      const bn = $(".pdp-buy-now");
      if (bn) syncBuyNowToOL(bn);

      /* Keep Subscribe & Save in step with the newly selected variant — its
         price/saving/plan were baked for the initial variant only, so a
         switch must re-point them or the shown numbers go stale. */
      const subToggle = $("[data-sub-toggle]");
      if (subToggle) {
        const onetimeOpt = subToggle.querySelector('.sub-opt[data-purchase="onetime"] .sub-desc');
        if (onetimeOpt && price) onetimeOpt.textContent = price;
        const subOpt = subToggle.querySelector('.sub-opt[data-purchase="subscribe"]');
        if (subOpt) {
          const subPrice = b.dataset.subPrice;
          const subSavingPct = parseInt(b.dataset.subSavingPct || "0", 10);
          const subPlan = b.dataset.subPlan || "";
          subOpt.dataset.sellingPlan = subPlan;
          const subDesc = subOpt.querySelector(".sub-desc");
          if (subDesc && subPrice) subDesc.textContent = `${subPrice} / delivery`;
          let saveEl = subOpt.querySelector(".sub-save");
          if (subSavingPct > 0) {
            if (!saveEl) {
              saveEl = document.createElement("span");
              saveEl.className = "sub-save";
              subOpt.insertBefore(saveEl, subOpt.firstChild);
            }
            saveEl.textContent = `Save ${subSavingPct}%`;
          } else if (saveEl) {
            saveEl.remove();
          }
          if (atc && subOpt.classList.contains("active")) {
            const desc = subOpt.querySelector(".sub-desc");
            atc.textContent = desc ? "Subscribe — " + desc.textContent.replace(/\s*\/.*$/, "") + " / delivery" : "Subscribe & Save";
          }
        }
        syncSubToggleUI();
      }
      try {
        const u = new URL(location.href);
        u.searchParams.set("variant", vid);
        history.replaceState(null, "", u);
      } catch (_) {}
    })
  );

  $$(".js-qty-step").forEach((b) =>
    b.addEventListener("click", () => {
      const i = $("#qty");
      if (!i) return;
      i.value = Math.max(1, (parseInt(i.value, 10) || 1) + parseInt(b.dataset.step, 10));
    })
  );

  const activateTab = (b, focus = false) => {
    const tabs = $$(".js-tab", b.closest(".tabs"));
    const panels = $$(".tab-body", b.closest(".tabs"));
    tabs.forEach((x) => {
      const active = x === b;
      x.classList.toggle("active", active);
      x.setAttribute("aria-selected", active ? "true" : "false");
      x.tabIndex = active ? 0 : -1;
    });
    panels.forEach((x) => {
      const active = x.dataset.tab === b.dataset.tab;
      x.classList.toggle("active", active);
      x.hidden = !active;
    });
    if (focus) b.focus();
  };
  $$(".js-tab").forEach((b) => {
    b.addEventListener("click", () => activateTab(b));
    b.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      const tabs = $$(".js-tab", b.closest(".tabs"));
      const index = tabs.indexOf(b);
      const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      activateTab(tabs[next], true);
    });
  });

  $$(".js-thumb").forEach((t) =>
    t.addEventListener("click", () => {
      $$(".js-thumb").forEach((x) => x.classList.remove("active"));
      t.classList.add("active");
      const main = $("#pdpMainImg");
      if (main) {
        main.removeAttribute("srcset");
        main.removeAttribute("sizes");
        main.src = t.dataset.src;
      }
    })
  );

  /* Sticky add-on for the sticky ATC button (CTA variant doesn't need this) */
  document.body.addEventListener("click", async (e) => {
    const btn = e.target.closest('[data-action="sticky-add"]');
    if (!btn || btn.dataset.busy === "1") return;
    e.preventDefault();
    btn.dataset.busy = "1";
    try {
      const mainAdd = $(".pdp-buybox .js-add-to-cart");
      const qty = parseInt($("#qty")?.value || 1, 10);
      const res = await cartAdd(btn.dataset.variantId, qty, selectedSellingPlan(mainAdd));
      await openCart();
      if (!res._ok) showCartNotice(res.description || res.message || "Couldn't add that to your basket — please check stock and try again.");
    } finally {
      delete btn.dataset.busy;
    }
  });

  /* Initial cart pip */
  try {
    const cart = await cartFetch();
    setPip(cart.item_count);
  } catch (_error) {
    setPip(0);
  }

  /* Page-level sticky CTA wiring.
     Each template can declare data-sticky-cta on <body> to opt-in. */
  const body = document.body;
  if (body.dataset.stickyCta === "home") {
    initStickyCTA(".hero-cta", [
      { href: "/collections/all-formulations", label: "Shop the range",    cls: "btn--forest btn-arrow" },
      { href: "/pages/quiz",       label: "Find my formulation", cls: "btn--ghost" },
    ]);
  }
});

/* Expose helpers for inline handlers and PDP template. */
window.cartFetch = cartFetch;
window.cartAdd = cartAdd;
window.cartChange = cartChange;
window.cartLineChange = cartLineChange;
window.renderCart = renderCart;
window.openCart = openCart;
window.closeCart = closeCart;
window.openChat = openChat;
window.closeChat = closeChat;
window.sendChat = sendChat;
window.initStickyProduct = initStickyProduct;
window.initStickyCTA = initStickyCTA;


/* ─── Subscribe & Save toggle (PDP) ─── */
function syncSubToggleUI() {
  const toggle = document.querySelector("[data-sub-toggle]");
  if (!toggle) return;
  const active = toggle.querySelector(".sub-opt.active");
  if (!active) return;
  const subscribe = active.dataset.purchase === "subscribe";
  const freq = document.querySelector("[data-sub-freq]");
  const addBtn = document.querySelector(".pdp-buybox .js-add-to-cart");
  const stickyAdd = document.querySelector('[data-action="sticky-add"]');
  const stickyPrice = document.querySelector(".sticky-atc .price");
  const desc = active.querySelector(".sub-desc");
  const price = desc ? desc.textContent.trim().replace(/\s*\/.*$/, "") : "";
  if (freq) freq.hidden = !subscribe;
  if (addBtn && !addBtn.disabled) addBtn.textContent = subscribe ? `Subscribe — ${price} / delivery` : `Add to basket — ${price}`;
  if (stickyAdd && !stickyAdd.disabled) stickyAdd.textContent = subscribe ? "Subscribe" : "Add";
  if (stickyPrice && price) stickyPrice.textContent = price;
}
function initSubToggle() {
  const toggle = document.querySelector("[data-sub-toggle]");
  if (!toggle) return;
  toggle.querySelectorAll(".sub-opt").forEach((opt) => {
    opt.addEventListener("click", () => {
      toggle.querySelectorAll(".sub-opt").forEach((o) => { o.classList.remove("active"); o.setAttribute("aria-pressed", "false"); });
      opt.classList.add("active");
      opt.setAttribute("aria-pressed", "true");
      syncSubToggleUI();
    });
  });
  syncSubToggleUI();
}

/* ─── Cart cross-sell (bestsellers not already in cart) ─── */
let _bestsellerCache = null;
async function fetchBestsellers() {
  if (_bestsellerCache) return _bestsellerCache;
  try {
    const r = await fetch("/collections/bestsellers/products.json?limit=8", { headers: { Accept: "application/json" } });
    const j = await r.json();
    _bestsellerCache = j.products || [];
  } catch (e) { _bestsellerCache = []; }
  return _bestsellerCache;
}
async function renderCartUpsell(cart) {
  const el = document.getElementById("cartUpsell");
  if (!el) return;
  const inCart = new Set(cart.items.map((i) => i.product_id));
  const products = await fetchBestsellers();
  const picks = products.filter((p) => !inCart.has(p.id)).slice(0, 2);
  if (!picks.length) { el.innerHTML = ""; return; }
  el.innerHTML =
    '<div class="cart-upsell-head">Popular add-ons</div>' +
    picks.map((p) => {
      const v = (p.variants || [])[0] || {};
      const img = (p.images || [])[0]?.src || "";
      const price = formatR(Math.round(parseFloat(v.price || 0) * 100));
      const name = (p.title || "").split(" - ").pop();
      return '<div class="cart-upsell-item">' +
        (img ? '<img src="' + img + '&width=120" alt="" loading="lazy">' : '') +
        '<div class="cart-upsell-info"><div class="cart-upsell-name">' + name + '</div><div class="cart-upsell-price">' + price + '</div></div>' +
        '<button type="button" class="cart-upsell-add" onclick="vividCartAdd(' + v.id + ')" aria-label="Add ' + name + '">Add</button>' +
        '</div>';
    }).join("");
}

/* ─── Mobile nav drawer ─── */
let mobNavOpener = null;
function openMobNav() {
  const nav = document.getElementById("mobnav");
  if (!nav?.classList.contains("open") && document.activeElement instanceof HTMLElement) mobNavOpener = document.activeElement;
  lockStorefrontScroll();
  document.getElementById("mobnavScrim")?.classList.add("open");
  if (nav) nav.inert = false;
  if (nav) isolateStorefrontOverlay(nav, document.getElementById("mobnavScrim"));
  nav?.classList.add("open");
  nav?.setAttribute("aria-hidden", "false");
  document.getElementById("navBurger")?.setAttribute("aria-expanded", "true");
  if (nav) focusDialog(nav);
}
function closeMobNav() {
  const nav = document.getElementById("mobnav");
  if (!nav?.classList.contains("open")) return;
  document.getElementById("mobnavScrim")?.classList.remove("open");
  nav.classList.remove("open");
  nav.setAttribute("aria-hidden", "true");
  nav.inert = true;
  document.getElementById("navBurger")?.setAttribute("aria-expanded", "false");
  restoreStorefrontIsolation();
  window.setTimeout(unlockStorefrontScroll, 300);
  if (mobNavOpener?.isConnected) mobNavOpener.focus();
  mobNavOpener = null;
}
function initMobileNav() {
  const burger = document.getElementById("navBurger");
  if (burger) burger.addEventListener("click", openMobNav);
  document.getElementById("mobnavScrim")?.addEventListener("click", closeMobNav);
  document.getElementById("mobnavClose")?.addEventListener("click", closeMobNav);
  document.querySelectorAll("#mobnav a").forEach((a) => a.addEventListener("click", closeMobNav));
  document.addEventListener("keydown", (event) => {
    if (document.querySelector(".v-shelf__qv.open")) return;
    const nav = document.getElementById("mobnav");
    const drawer = document.getElementById("drawer");
    if (nav?.classList.contains("open")) trapDialogKeydown(event, nav, closeMobNav);
    else if (drawer?.classList.contains("open")) trapDialogKeydown(event, drawer, closeCart);
  });
  document.addEventListener("focusin", (event) => {
    const nav = document.getElementById("mobnav");
    const drawer = document.getElementById("drawer");
    const active = nav?.classList.contains("open") ? nav : drawer?.classList.contains("open") ? drawer : null;
    if (active && !active.contains(event.target)) focusDialog(active);
  });
}
window.closeMobNav = closeMobNav;
